import { InitData } from '../../core'

interface Props {
  initData: InitData
  // Per-instance override of the banner title (data-override-title="...")
  overrideTitle?: string | null
}

/**
 * CampaignBannerWidget — wide promotional banner.
 *
 * Layout (per HS-native marketing banner):
 *   - Full-width banner with optional background image (overlay) or solid color
 *   - Centered content: large title (h2, ~2rem), subtitle, CTA button
 *   - Filled CTA with contrasting color (padding 0.9rem 2rem)
 *   - Vertical 3rem / horizontal 2rem padding
 *   - Border radius from var(--yoga-radius)
 *   - textColor controls title/subtitle (falls back to white when bg image is set)
 *
 * Renders nothing if banner is disabled or has no title.
 */
export function CampaignBannerWidget({ initData, overrideTitle = null }: Props) {
  const banner = initData.campaignBanner
  const title = overrideTitle || banner?.title || null

  if (!banner || !banner.enabled || !title) {
    return null
  }

  const hasImage = Boolean(banner.backgroundImageUrl)
  const bgColor = banner.backgroundColor || '#6366f1'
  const textColor = banner.textColor || '#ffffff'
  const subtitleColor = banner.textColor || (hasImage ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.92)')

  const bannerStyle: Record<string, string> = {
    position: 'relative',
    padding: '3rem 2rem',
    borderRadius: 'var(--yoga-radius, 0)',
    textAlign: 'center',
    color: textColor,
    backgroundColor: bgColor,
    overflow: 'hidden',
    maxWidth: '1100px',
    margin: '0 auto',
  }

  if (hasImage) {
    bannerStyle.backgroundImage = `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${banner.backgroundImageUrl})`
    bannerStyle.backgroundSize = 'cover'
    bannerStyle.backgroundPosition = 'center'
    bannerStyle.backgroundRepeat = 'no-repeat'
  }

  const isExternalLink = (href: string): boolean => {
    if (/^(mailto:|tel:|sms:)/i.test(href)) {
      return true
    }
    if (!/^https?:\/\//i.test(href)) {
      return false
    }
    if (typeof window === 'undefined') {
      return true
    }
    try {
      const url = new URL(href)
      return url.hostname !== window.location.hostname
    } catch {
      return false
    }
  }

  const openInNewTab = banner.buttonLink ? isExternalLink(banner.buttonLink) : false

  return (
    <div>
      <style>{bannerStyles}</style>
      <div class="hsw-campaign-banner" style={bannerStyle}>
        <div class="hsw-campaign-inner">
          <h2 class="hsw-campaign-title" style={{ color: textColor }}>
            {title}
          </h2>
          {banner.subtitle && (
            <p class="hsw-campaign-subtitle" style={{ color: subtitleColor }}>
              {banner.subtitle}
            </p>
          )}
          {banner.buttonText && banner.buttonLink && (
            <a
              href={banner.buttonLink}
              {...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              class="hsw-campaign-cta"
              // Color-flip CTA against the banner so the button is always
              // readable, regardless of admin-chosen banner colors. Matches
              // CMS CampaignBannerWidget behavior.
              style={{ backgroundColor: textColor, color: bgColor, borderColor: textColor }}
            >
              {banner.buttonText}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

const bannerStyles = `
  .hsw-campaign-banner {
    transition: all .3s;
  }
  .hsw-campaign-inner {
    max-width: 720px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    position: relative;
    z-index: 1;
  }
  .hsw-campaign-title {
    font-size: 2rem;
    font-weight: 700;
    margin: 0;
    line-height: 1.2;
    letter-spacing: -0.02em;
  }
  .hsw-campaign-subtitle {
    font-size: 1.05rem;
    margin: 0;
    line-height: 1.5;
    max-width: 600px;
  }
  .hsw-campaign-cta {
    display: inline-block;
    margin-top: 0.75rem;
    padding: 0.9rem 2rem;
    border-radius: var(--yoga-radius, 0);
    text-decoration: none;
    font-weight: 600;
    font-size: 0.95rem;
    letter-spacing: 0.01em;
    transition: all .3s;
    border: 1px solid transparent;
  }
  .hsw-campaign-cta:hover {
    filter: brightness(0.92);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  }
  @media (max-width: 640px) {
    .hsw-campaign-banner {
      padding: 2rem 1rem !important;
    }
    .hsw-campaign-title {
      font-size: 1.35rem;
    }
    .hsw-campaign-subtitle {
      font-size: 0.95rem;
    }
    .hsw-campaign-cta {
      padding: 0.75rem 1.5rem;
      font-size: 0.9rem;
    }
  }
`
